// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { LocationType } from 'domains/models/event_type/locationType';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import Button from 'presentations/views/components/atoms/Button';
import { black, white } from 'presentations/views/styles/color';
import { SyntheticEvent } from 'react';
import { Trail } from 'presentations/views/components/atoms/Transition';
import {
  formatLocationName,
  locationText,
} from 'presentations/views/helpers/event_type';

export type Props = {
  onSelectLocationType: (locationType?: LocationType) => void;
  selectedLocationId: LocationType | null;
  locationIds: LocationType[];
  isLoading?: boolean;
  customLocation: string | null;
  address: string | null;
  color: string;
};

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  return (
    <StyledContainer>
      <StyledHeaderNavigation>
        <StyledHeader>{t('Location')}</StyledHeader>
      </StyledHeaderNavigation>
      <StyledLocationList isOpen>
        {props.locationIds.map((locationType) => (
          <StyledButtonContainer key={locationType}>
            <StyledLocationButton
              isLoading={props.isLoading}
              loadingVariant={props.isLoading ? 'skeleton' : undefined}
              isDisableRipple={true}
              variant="outlined"
              onClick={(e: SyntheticEvent) =>
                props.onSelectLocationType(locationType)
              }
              size="large"
              color="inherit"
              borderColor={props.color}
            >
              <StyledButtonInner>
                <StyledLocationImage
                  src={`locations/${locationType}.svg`}
                  alt={locationText(locationType)}
                />
                <StyledName>
                  {formatLocationName({
                    locationType,
                    customLocation: props.customLocation,
                    address: props.address,
                  })}
                </StyledName>
              </StyledButtonInner>
            </StyledLocationButton>
          </StyledButtonContainer>
        ))}
        {props.locationIds.length === 0 && (
          <StyledButtonContainer>
            <StyledLocationButton
              isLoading={props.isLoading}
              loadingVariant={props.isLoading ? 'skeleton' : undefined}
              isDisableRipple={true}
              variant="outlined"
              onClick={(e: SyntheticEvent) => props.onSelectLocationType()}
              size="large"
              color="inherit"
              borderColor={props.color}
            >
              <StyledButtonInner>
                <StyledLocationImage
                  src={`locations/1.svg`}
                  alt={t(`locationType_1`)}
                />
                <StyledName>{t('Location Unspecified')}</StyledName>
              </StyledButtonInner>
            </StyledLocationButton>
          </StyledButtonContainer>
        )}
      </StyledLocationList>
    </StyledContainer>
  );
}

const StyledContainer = styled.div``;
const StyledHeaderNavigation = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const StyledHeader = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.48;
`;
const StyledLocationList = styled(Trail)`
  margin-top: ${spacing(40)};
  ${mediaMax.tablet} {
    margin-top: 0;
  }
`;
const StyledButtonContainer = styled.div`
  margin-top: ${spacing(16)};
`;
const StyledButtonInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;
const StyledLocationButton = styled(Button)<{ borderColor: string }>`
  width: 100%;
  border-color: ${({ borderColor }) => borderColor};
  color: ${black};
  background: ${white};
  font-weight: ${fontWeight('normal')};
`;
const StyledName = styled.span`
  text-align: left;
  margin-left: ${spacing(8)};
`;
const StyledLocationImage = styled(Image)`
  width: 24px;
  height: 24px;
`;
